import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import CommonModal from 'src/components/CommonModal';
import InputTextArea from 'src/components/InputTextArea';
import { logError } from 'src/services/LoggingService';
import { updateOrderLegNotes } from 'src/services/OrderService';
import WalToast from 'src/utils/WalToast';

// The two instruction fields the customer may edit, keyed by the noteType the
// PATCH /orders/leg/notes/:id endpoint expects. Each writes the matching
// order_legs column, which is what the WAL order detail page's Order
// Instructions card renders — so an edit here shows up for ops immediately.
const NOTE_TYPE_COPY = {
    pickup: {
        addTitle: 'Orders.Add_Pickup_Instructions',
        updateTitle: 'Orders.Update_Pickup_Instructions',
        addLabel: 'Orders.Enter_Pickup_Instructions_Label',
        updateLabel: 'Orders.Update_Pickup_Instructions_Label',
        placeholder: 'Orders.Add_Pickup_Instructions_Placeholder',
    },
    delivery: {
        addTitle: 'Orders.Add_Delivery_Instructions',
        updateTitle: 'Orders.Update_Delivery_Instructions',
        addLabel: 'Orders.Enter_Delivery_Instructions_Label',
        updateLabel: 'Orders.Update_Delivery_Instructions_Label',
        placeholder: 'Orders.Add_Delivery_Instructions_Placeholder',
    },
} as const;

const MAX_INSTRUCTIONS_LENGTH = 500;

interface IProps {
    handleClose: (status: boolean) => void;
    orderLegId: number;
    noteType: 'pickup' | 'delivery';
    initialInstructions?: string;
    onSuccess?: (noteType: 'pickup' | 'delivery', instructions: string, activity?: any) => void;
}

const LegInstructionsModal = ({ handleClose, orderLegId, noteType, initialInstructions = '', onSuccess }: IProps) => {
    const { t } = useTranslation();
    const copy = NOTE_TYPE_COPY[noteType];
    const [instructions, setInstructions] = useState(initialInstructions);
    const [isLoading, setIsLoading] = useState(false);
    // If the leg already carries instructions, the modal acts as an "Update" flow rather than "Add"
    const isUpdateMode = Boolean(initialInstructions);

    const handleChange = (e: React.ChangeEvent<HTMLTextAreaElement>) => {
        const { value } = e.target;
        if (value !== '' && value.trim() === '') return;
        setInstructions(value);
    };

    const handleSubmit = async () => {
        setIsLoading(true);

        try {
            const trimmedInstructions = instructions.trim();
            const response = await updateOrderLegNotes(orderLegId, noteType, trimmedInstructions);
            WalToast.success(t('Orders.Instructions_submitted'));
            // Axios interceptor returns response.data for 200 — activity is in response.data.activity
            const activity = response?.data?.data?.activity ?? response?.data?.activity;
            onSuccess?.(noteType, trimmedInstructions, activity);
            handleClose(false);
        } catch (err: any) {
            await logError(err, {
                errorMessage: err?.message || `Error saving ${noteType} instructions`,
                errorLocation: 'LegInstructionsModal: handleSubmit',
                storeInDatabase: true,
                logToConsole: true,
            });
            WalToast.error(err?.response?.data?.message || t('Orders.Instructions_failed'));
        } finally {
            setIsLoading(false);
        }
    };

    return (
        <CommonModal
            title={t(isUpdateMode ? copy.updateTitle : copy.addTitle)}
            handleClose={() => handleClose(false)}
            size="max-w-[600px]"
            isOverflow={true}
            secondaryBtnText={t('Common.Cancel')}
            secondaryBtnOnClick={() => handleClose(false)}
            secondaryBtnDisabled={isLoading}
            primaryBtnText={isUpdateMode ? t('Common.Update') : t('Common.Submit')}
            primaryBtnOnClick={handleSubmit}
            primaryBtnLoading={isLoading}
            primaryBtnDisabled={isLoading || !instructions.trim() || instructions.trim() === initialInstructions.trim()}
        >
            <div className="p-4">
                <InputTextArea
                    inputName={`${noteType}Instructions`}
                    label={t(isUpdateMode ? copy.updateLabel : copy.addLabel)}
                    placeholder={t(copy.placeholder)}
                    value={instructions}
                    onChangeFunc={handleChange}
                    labelClassName="block mb-1.5"
                    labelWrapClass="flex justify-between items-center"
                    labelIconRight={
                        <span className="text-grayLight600 text-xs font-normal mb-1.5">
                            {instructions.length}/{MAX_INSTRUCTIONS_LENGTH}
                        </span>
                    }
                    className="h-[98px]"
                    maxLength={MAX_INSTRUCTIONS_LENGTH}
                    disabled={isLoading}
                />
            </div>
        </CommonModal>
    );
};

export default LegInstructionsModal;
